import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, forwardRef, HostListener, ViewChild, AfterViewInit, OnChanges, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormControl } from '@angular/forms';
import { ApiService, ABCisApi } from 'src/app/handlers/apiHandler.service';
import { Dataset } from '../abcis-table/abcis-table.component';
import { Observable, Subject, of, from} from 'rxjs';
import { debounceTime, switchMap, catchError, map} from 'rxjs/operators';

@Component({
  selector: 'primeng-multiselect',
  standalone: false,
  templateUrl: './primeng-multiselect.component.html',
  styleUrls: ['./primeng-multiselect.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PrimengMultiselectComponent),
      multi: true
    }
  ]
})
export class PrimengMultiselectComponent implements OnInit, OnDestroy, ControlValueAccessor, AfterViewInit, OnChanges {

  // ========== INPUT PROPERTIES ==========
  @Input() setUrl: string; // API endpoint for data fetching
  @Input() columnset: Dataset[]; // Predefined data options
  @Input() addedParams = ''; // Additional API parameters
  @Input() isSingle = false; // Single vs multi-select mode
  @Input() placeholder = 'Select...'; // Input placeholder text
  @Input() loadInitialData = true; // Whether to load data on init
  @Input() isFilterOnly = false; // Filter mode vs add mode
  @Input() useExistingDataOnly = false; // Use only columnset data
  @Input() allowMultipleFromSearch = true; // Enable bulk selection UI
  @Input() enableSmartSearch = true; // Enable smart search features
  @Input() baseMaxResults = 500; // Maximum search results
  @Input() pageSize = 50; // Results per page
  @Input() preloadedCache: Dataset[] = []; // Pre-cached data
  @Input() isEditMode = false; // Edit mode flag

  // ========== OUTPUT EVENTS ==========
  @Output() onChange = new EventEmitter<any>(); // Selection change event

  // ========== VIEW REFERENCES ==========
  @ViewChild('autoComplete') autoComplete: any; // PrimeNG AutoComplete reference

  // ========== FORM CONTROLS ==========
  control = new FormControl(); // Main form control
  searchControl = new FormControl(); // Search input control

  // ========== DATA PROPERTIES ==========
  filteredOptions: Dataset[] = []; // Current filtered search results
  allOptions: Dataset[] = []; // All available options
  selectedItems: Dataset[] = []; // Currently selected items
  private selectedItemsCache: Dataset[] = []; // Cache for selected items
  private allSearchResults: Dataset[] = []; // All search results (for pagination)

  // ========== STATE PROPERTIES ==========
  isLoading = false; // Loading state for search
  filterGroup: {[id: number]: boolean} = {}; // Selected items map
  selectCount = 0; // Count of selected items
  lastSearchTerm = ''; // Last search query
  showNoResults = false; // Show no results message
  showDropdown = false; // Dropdown visibility state

  // ========== PAGINATION PROPERTIES ==========
  private currentPage = 1; // Current page number
  private hasMoreResults = false; // More results available flag
  private totalResults = 0; // Total search results count
  private isLoadingMore = false; // Loading more results state

  // ========== BULK SELECTION PROPERTIES ==========
  dropdownSelection: {[id: number]: boolean} = {}; // Temporary dropdown selections
  bulkSelectionCount = 0; // Count of bulk selected items

  // ========== PRIVATE PROPERTIES ==========
  private loadingPromises: Map<string, Promise<Dataset[]>> = new Map(); // Track loading promises
  private isInitializing = false; // Prevent recursive updates during init
  private onTouched = () => {}; // Form touched callback
  private onChanged = (value: any) => {}; // Form change callback
  private searchSubject = new Subject<string>(); // Debounced search subject
  private hasInitialized = false; // Initialization flag

  // ========== HOST LISTENERS ==========
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    // Close dropdown when clicking outside
    const target = event.target as HTMLElement;
    const wrapper = target.closest('.primeng-multiselect-wrapper');
    if (!wrapper) {
      this.showDropdown = false;
    }
  }

  // ========== CONSTRUCTOR ==========
  constructor(private apiService: ApiService) {
    // Setup debounced search with error handling
    this.searchSubject.pipe(
      debounceTime(1000),
      switchMap(query => this.performSearch(query, 0)),
      catchError(error => {
        this.isLoading = false;
        this.showNoResults = true;
        return of({ results: [], total: 0, hasMore: false });
      })
    ).subscribe(response => {
      this.handleSearchResponse(response);
    });
  }

  // ========== LIFECYCLE METHODS ==========

  ngOnChanges(changes: SimpleChanges) {
    // Handle columnset changes after table data is loaded
    if (changes['columnset'] && changes['columnset'].currentValue) {
      this.allOptions = [...this.columnset];

      // For single select with existing data only, immediately set filtered options
      if (this.isSingle && this.useExistingDataOnly) {
        this.filteredOptions = [...this.columnset];
      }

      // Trigger change detection
      setTimeout(() => {
        if (this.autoComplete && this.filteredOptions.length > 0) {
          // Update the autocomplete's suggestions
          this.autoComplete.suggestions = [...this.filteredOptions];
        }
      }, 0);
    }
  }

  ngOnInit() {
    // Initialize component with existing data if available
    if (!this.hasInitialized) {
      this.hasInitialized = true;
      if (this.useExistingDataOnly && this.columnset && this.columnset.length > 0) {
        this.allOptions = [...this.columnset];
        if (this.isSingle) {
          this.filteredOptions = [...this.columnset];
        }
      }
    }
  }

  ngAfterViewInit() {
    // Setup dropdown enhancement when autocomplete shows
    if (this.autoComplete) {
      this.autoComplete.onShow.subscribe(() => {
        setTimeout(() => this.enhanceDropdown(), 50);
      });
    }
  }

  ngOnDestroy() {
    // Cleanup subscriptions and promises
    this.searchSubject.complete();
    this.loadingPromises.clear();
  }

  // ========== CONTROL VALUE ACCESSOR METHODS ==========
  writeValue(obj: any): void {
    // Set form value from external source
    if (this.isInitializing) return;

    this.isInitializing = true;
    try {
      this.filterGroup = {};
      this.selectCount = 0;
      this.selectedItemsCache = [];

      // Clear the search input when value is cleared
      if (obj == null || obj === '') {
        this.searchControl.setValue('');
        this.clearSearchState();
        if (this.autoComplete) {
          this.autoComplete.writeValue('');
        }
      } else {
        if (this.isSingle) {
          const value = Array.isArray(obj) ? obj[0] : obj;
          if (value != null) {
            this.filterGroup[value] = true;
            this.selectCount = 1;
          }
        } else {
          const values = Array.isArray(obj) ? obj : [obj];
          values.forEach(val => {
            if (val != null) {
              this.filterGroup[val] = true;
              this.selectCount++;
            }
          });
        }
      }
      this.updateSelectedItems();
    } finally {
      setTimeout(() => { this.isInitializing = false; }, 0);
    }
  }

  registerOnChange(fn: any): void {
    // Register form change callback
    this.onChanged = fn;
  }

  registerOnTouched(fn: any): void {
    // Register form touched callback
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // Enable/disable form controls
    if (isDisabled) {
      this.control.disable();
      this.searchControl.disable();
    } else {
      this.control.enable();
      this.searchControl.enable();
    }
  }

  // ========== PUBLIC GETTER METHODS ==========
  getSelectedItems(): Dataset[] {
    // Get currently selected items with fallback
    if (this.selectedItems && this.selectedItems.length > 0) {
      return this.selectedItems;
    }

    const items: Dataset[] = [];
    for (const key in this.filterGroup) {
      if (this.filterGroup[key] === true) {
        const numericKey = Number(key);
        if (!isNaN(numericKey)) {
          let item = this.findItemInDatasets(numericKey);
          if (!item) {
            item = { value: numericKey, label: `${numericKey}` };
          }
          items.push(item);
        }
      }
    }
    return items;
  }

  getTruncatedLabel(label: string): string {
    // Truncate long labels for display
    if (!label) return '';
    return label.length <= 13 ? label : label.substring(0, 13) + '...';
  }

  getHoverTooltip(): string {
    // Get tooltip text for selected items container
    const count = this.getSelectedItems().length;
    return count <= 3 ? 'Selected items' : `${count} items selected - hover to see all`;
  }

  getEffectivePlaceholder(): string {
    // Get appropriate placeholder text based on mode
    if (this.useExistingDataOnly) {
      return this.isSingle ? 'Select from existing...' : 'Filter from existing...';
    }
    return this.isSingle ? 'Search and select one...' : 'Search...';
  }

  getEmptyMessage(): string {
    // Get empty state message based on current state
    if (this.useExistingDataOnly && this.isSingle) {
      if (!this.lastSearchTerm && this.filteredOptions.length > 0) {
        return 'Select an option...';
      }
      if (!this.lastSearchTerm) {
        return 'No options available';
      }
    }

    if (!this.lastSearchTerm) {
      return this.useExistingDataOnly ? 'Start typing to filter...' : 'Type at least 2 characters to search...';
    }
    if (this.isLoading) return 'Searching...';
    if (this.showNoResults) return `No results found for "${this.lastSearchTerm}"`;
    return 'No options available';
  }

  getInputTooltip(): string {
    // Get tooltip for input field
    const placeholder = this.getEffectivePlaceholder();
    const selectedCount = this.getSelectedItems().length;
    if (selectedCount > 0) {
      return `${placeholder} | ${selectedCount} item${selectedCount > 1 ? 's' : ''} selected`;
    }
    return placeholder;
  }

  // ========== SEARCH METHODS ==========
  search(event: any): void {
    // Handle search input with debouncing
    const query = event.query?.trim() || '';

    if (this.useExistingDataOnly) {
      // Ensure we have the latest columnset data
      if (this.columnset && this.columnset.length > 0 && this.allOptions.length === 0) {
        this.allOptions = [...this.columnset];
      }

      this.filterExistingData(query);
      // ✅ Always show dropdown for single select, even with empty query
      if (this.isSingle || this.filteredOptions.length > 0) {
        if (this.autoComplete) {
          setTimeout(() => {
            this.autoComplete.show();
            setTimeout(() => this.enhanceDropdown(), 50);
          }, 100);
        }
      }
      return;
    }

    if (query.length >= 2) {
      this.isLoading = true;
      this.lastSearchTerm = query;
      this.showNoResults = false;

      if (this.autoComplete) {
        setTimeout(() => {
          this.autoComplete.show();
          setTimeout(() => this.enhanceDropdownWithLoading(), 50);
        }, 100);
      }
      this.searchSubject.next(query);
    } else {
      this.clearSearchState();
    }

    this.updateFormValue();
    this.updateSelectedItems();
    this.emitChange();
    this.onTouched();
  }

  private performSearch(query: string, page: number): Observable<{results: Dataset[], total: number, hasMore: boolean}> {
    // Execute API search with pagination
    if (!this.setUrl || !query || query.length < 2) {
      return of({ results: [], total: 0, hasMore: false });
    }

    const maxResults = this.baseMaxResults;
    const cleanParams = this.cleanupParams(this.addedParams || '');
    const encodedQuery = encodeURIComponent(query.trim());
    const url = `${this.setUrl}?mode=filter&values=${encodedQuery}&page=${page}&pageSize=${this.pageSize}&maxResults=${maxResults}${cleanParams}`;

    return from(this.apiService.getABCisRequest<any>(ABCisApi.Support, url))
      .pipe(
        map(response => this.parseApiResponse(response.body)),
        catchError(error => of({ results: [], total: 0, hasMore: false }))
      );
  }

  private filterExistingData(query: string): void {
    // Filter existing columnset data locally
    this.lastSearchTerm = query;
    if (!query) {
      this.filteredOptions = [...this.allOptions];
    } else {
      this.filteredOptions = this.allOptions
        .filter(option =>
          option.label.toLowerCase().includes(query.toLowerCase()) // ← ONLY FILTER BY SEARCH TERM
        );
    }
    this.showNoResults = this.filteredOptions.length === 0;
  }

  private handleSearchResponse(response: {results: Dataset[], total: number, hasMore: boolean}): void {
    // Process search results and update UI
    this.currentPage = 1;
    this.allSearchResults = response.results || [];
    this.filteredOptions = [...this.allSearchResults];
    this.totalResults = response.total || 0;
    this.hasMoreResults = response.hasMore || false;
    this.showNoResults = this.filteredOptions.length === 0;
    this.isLoading = false;

    if (this.filteredOptions.length > 0 && this.autoComplete) {
      setTimeout(() => {
        this.autoComplete.show();
        setTimeout(() => {
          this.enhanceDropdown();
          this.forceUpdateResultCount();
        }, 0);
      }, 0);
    }
  }

  loadMoreResults(): void {
    // Load next page of search results
    if (!this.hasMoreResults || this.isLoadingMore || !this.lastSearchTerm) return;

    this.isLoadingMore = true;
    this.currentPage++;

    this.performSearch(this.lastSearchTerm, this.currentPage).subscribe({
      next: (response) => {
        const newResults = response.results || [];
        this.allSearchResults = [...this.allSearchResults, ...newResults];
        this.filteredOptions = [...this.allSearchResults];
        this.hasMoreResults = response.hasMore;
        this.isLoadingMore = false;
        setTimeout(() => {
          this.enhanceDropdown();
          this.forceUpdateResultCount();
        }, 0);
      },
      error: () => {
        this.isLoadingMore = false;
        this.currentPage--;
        this.hasMoreResults = false;
      }
    });
  }

  private clearSearchState(): void {
    // Reset all search-related state
    this.filteredOptions = [];
    this.allSearchResults = [];
    this.isLoading = false;
    this.isLoadingMore = false;
    this.showNoResults = false;
    this.lastSearchTerm = '';
    this.currentPage = 1;
    this.hasMoreResults = false;
    this.totalResults = 0;
  }

  // ========== DROPDOWN ENHANCEMENT METHODS ==========
  private enhanceDropdown(): void {
    const panel = document.querySelector('.p-autocomplete-overlay');
    if (!panel) return;

    // Clear any existing enhancements first
    const existingHeaders = panel.querySelectorAll('.bulk-header, .single-header, .basic-header');
    existingHeaders.forEach(header => header.remove());

    if (this.isSingle) {
      this.addSingleSelectHeader(panel);
      this.addSingleSelectUI(panel);
    } else if (this.allowMultipleFromSearch) {
      this.addBulkHeader(panel);
      this.addCheckboxes(panel);
      // Force update checkboxes after adding them
      setTimeout(() => this.updateCheckboxes(), 0);
    } else {
      this.addBasicResultsHeader(panel);
      const items = panel.querySelectorAll('.p-autocomplete-option');
      items.forEach((item, index) => {
        const dataset = this.filteredOptions[index];
        if (dataset) {
          (item as HTMLElement).title = `Value: ${dataset.value}`;
        }
      });
    }

    if (this.hasMoreResults) {
      this.addLoadMoreButton(panel);
    }
  }

  private enhanceDropdownWithLoading(): void {
    // Show loading state in dropdown
    const panel = document.querySelector('.p-autocomplete-overlay');
    if (!panel) return;

    const existingHeader = panel.querySelector('.bulk-header, .basic-header');
    if (existingHeader) existingHeader.remove();

    const header = document.createElement('div');
    header.className = this.isSingle ? 'basic-header loading' : 'bulk-header loading';
    header.innerHTML = `
      <div class="${this.isSingle ? 'basic-info' : 'bulk-info'}">
        <span class="results-count">Searching...</span>
      </div>
    `;

    const listContainer = panel.querySelector('.p-autocomplete-list-container');
    if (listContainer) {
      panel.insertBefore(header, listContainer);
    }
  }

  private addBasicResultsHeader(panel: Element): void {
    // Add simple results header for basic mode
    const existingHeader = panel.querySelector('.bulk-header, .single-header, .basic-header');
    if (existingHeader) existingHeader.remove();

    const visibleCount = this.filteredOptions.length;

    const header = document.createElement('div');
    header.className = 'basic-header';
    header.innerHTML = `
      <div class="basic-info">
        <span class="results-count">${visibleCount} results</span>
        <span class="instruction">${this.isFilterOnly ? 'Click to filter' : 'Click to select'}</span>
      </div>
    `;

    const itemsList = panel.querySelector('.p-autocomplete-items');
    if (itemsList) {
      panel.insertBefore(header, itemsList);
    }
  }

  private addSingleSelectHeader(panel: Element): void {
    // Add header for single select mode
    const existingHeader = panel.querySelector('.bulk-header, .single-header');
    if (existingHeader) existingHeader.remove();

    const visibleCount = this.filteredOptions.length;

    const header = document.createElement('div');
    header.className = 'single-header';
    header.innerHTML = `
      <div class="single-info">
        <span class="results-count">${visibleCount} results</span>
        <span class="instruction">Click to select</span>
      </div>
    `;

    const itemsList = panel.querySelector('.p-autocomplete-items');
    if (itemsList) {
      panel.insertBefore(header, itemsList);
    }
  }

  private addSingleSelectUI(panel: Element): void {
    // Add selection indicators for single select items
    const items = panel.querySelectorAll('.p-autocomplete-option');
    items.forEach((item, index) => {
      const htmlItem = item as HTMLElement;
      const dataset = this.filteredOptions[index];
      if (!dataset) return;

      // ✅ CHECK if indicator already exists to prevent duplicates
      if (htmlItem.querySelector('.single-select-indicator')) {
        return; // Skip if already enhanced
      }

      // Add tooltip with value
      htmlItem.title = `Value: ${dataset.value}`;

      const indicator = document.createElement('span');
      indicator.className = 'single-select-indicator';

      if (this.filterGroup[dataset.value]) {
        indicator.innerHTML = '✓';
        indicator.classList.add('selected');
        htmlItem.classList.add('already-selected');
      } else {
        indicator.innerHTML = '';
        indicator.classList.add('unselected');
      }

      htmlItem.insertBefore(indicator, htmlItem.firstChild);
    });
  }

  private addBulkHeader(panel: Element): void {
    // Add bulk selection header with action buttons
    const existingHeader = panel.querySelector('.bulk-header');
    if (existingHeader) existingHeader.remove();

    const visibleCount = this.filteredOptions.length;
    const selectedCount = this.getSelectedItems().length;

    const header = document.createElement('div');
    header.className = 'bulk-header';

    const selectAllText = this.isSingle ?
      'Select First' :
      `Select All (${this.getSelectableCount()})`;

    header.innerHTML = `
    <div class="bulk-info">
      <span class="results-count">${visibleCount} results</span>
      <span class="selected" ${selectedCount === 0 ? 'style="display: none;"' : ''}>
        • ${selectedCount} selected
      </span>
    </div>
    <div class="bulk-actions">
      <button type="button" class="btn-select-all" ${this.areAllSelectableSelected() ? 'disabled' : ''}>
        ${selectAllText}
      </button>
      <button type="button" class="btn-clear" ${selectedCount === 0 ? 'disabled' : ''}>
        Clear All
      </button>
    </div>
  `;

    // Attach event listeners to buttons
    header.querySelector('.btn-select-all')?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.selectAllVisible();
    });

    header.querySelector('.btn-clear')?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.clearAllSelections();
      this.updateBulkUI();
      setTimeout(() => this.updateCheckboxes(), 0);
    });

    const listContainer = panel.querySelector('.p-autocomplete-list-container');
    if (listContainer) {
      panel.insertBefore(header, listContainer);
    }
  }

  private areAllSelectableSelected(): boolean {
    const selectableItems = this.filteredOptions.filter(item => !this.filterGroup[item.value]);
    return selectableItems.length === 0;
  }

  private addCheckboxes(panel: Element): void {
    const items = panel.querySelectorAll('.p-autocomplete-option');
    items.forEach((item, index) => {
      if (item.querySelector('.checkbox')) return;

      const dataset = this.filteredOptions[index];
      if (!dataset) return;

      // Add tooltip with value
      (item as HTMLElement).title = `Value: ${dataset.value} - Click anywhere to toggle selection`;

      const checkboxWrapper = document.createElement('div');
      checkboxWrapper.className = 'checkbox-wrapper';

      const checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.className = 'checkbox';

      checkbox.checked = !!this.filterGroup[dataset.value];

      checkboxWrapper.appendChild(checkbox);

      // Handle checkbox direct clicks
      checkbox.addEventListener('click', (e) => {
        e.stopPropagation();
        this.toggleSelection(dataset);
      });

      // Handle checkbox wrapper clicks
      checkboxWrapper.addEventListener('click', (e) => {
        e.stopPropagation();
        e.preventDefault();
        this.toggleSelection(dataset);
      });

      // Handle clicks on the entire item (including text/label area)
      const itemElement = item as HTMLElement;
      itemElement.style.cursor = 'pointer';

      // Remove any existing PrimeNG event listeners that might close the dropdown
      itemElement.removeAttribute('ng-reflect-ng-class');

      // Override ALL click events on the item
      itemElement.addEventListener('click', (e) => {
        e.stopPropagation();
        e.preventDefault();

        // Always toggle selection regardless of where clicked
        this.toggleSelection(dataset);

        return false;
      }, true);

      // Prevent all mouse events that might trigger PrimeNG's selection
      itemElement.addEventListener('mousedown', (e) => {
        e.stopPropagation();
        e.preventDefault();
      }, true);

      itemElement.addEventListener('mouseup', (e) => {
        e.stopPropagation();
        e.preventDefault();
      }, true);

      // Prevent PrimeNG's focus events
      itemElement.addEventListener('focus', (e) => {
        e.stopPropagation();
        e.preventDefault();
      }, true);

      // Override any existing onclick handlers
      (itemElement as any).onclick = (e: Event) => {
        e.stopPropagation();
        e.preventDefault();
        this.toggleSelection(dataset);
        return false;
      };

      item.insertBefore(checkboxWrapper, item.firstChild);

      // Set initial visual state
      if (this.filterGroup[dataset.value]) {
        item.classList.add('selected-item');
      }
    });
  }

  private addLoadMoreButton(panel: Element): void {
    // Add load more button to dropdown bottom
    const existing = panel.querySelector('.load-more');
    if (existing) existing.remove();

    const button = document.createElement('button');
    button.className = 'load-more';
    button.textContent = this.isLoadingMore ? 'Loading...' : `Load ${this.pageSize} More`;
    button.disabled = this.isLoadingMore;

    button.addEventListener('click', (e) => {
      e.stopPropagation();
      this.loadMoreResults();
    });

    panel.appendChild(button);
  }

  private forceUpdateResultCount(): void {
    // Update result count in dropdown header
    const resultsCount = document.querySelector('.bulk-header .results-count, .basic-header .results-count') as HTMLElement;
    if (resultsCount) {
      const visibleCount = this.filteredOptions.length;
      resultsCount.textContent = `${visibleCount} results`;
    }
  }

  // ========== BULK SELECTION METHODS ==========
  private toggleSelection(item: Dataset): void {
    if (this.isSingle) {
      // For single select
      if (this.filterGroup[item.value]) {
        // If clicking the same item, deselect it
        this.filterGroup[item.value] = false;
        this.selectCount = 0;
        this.selectedItemsCache = this.selectedItemsCache.filter(cached => cached.value !== item.value);
      } else {
        // Clear all previous selections and select this one
        this.clearAllSelectionsWithoutEmit();
        this.filterGroup[item.value] = true;
        this.selectCount = 1;

        // Update caches immediately
        if (!this.selectedItemsCache.find(cached => cached.value === item.value)) {
          this.selectedItemsCache.push(item);
        }
        if (!this.allOptions.find(option => option.value === item.value)) {
          this.allOptions.push(item);
        }
      }
    } else {
      // For multi-select, toggle the selection
      if (this.filterGroup[item.value]) {
        this.filterGroup[item.value] = false;
        this.selectCount--;
        this.selectedItemsCache = this.selectedItemsCache.filter(cached => cached.value !== item.value);
      } else {
        this.filterGroup[item.value] = true;
        this.selectCount++;

        if (!this.selectedItemsCache.find(cached => cached.value === item.value)) {
          this.selectedItemsCache.push(item);
        }
        if (!this.allOptions.find(option => option.value === item.value)) {
          this.allOptions.push(item);
        }
      }
    }

    // Immediately update form and emit changes
    this.updateFormValue();
    this.updateSelectedItems();
    this.emitChange();
    this.onTouched();

    // Force update UI and checkboxes
    setTimeout(() => {
      this.updateBulkUI();
      this.updateCheckboxes();
    }, 10);
  }

  private selectAllVisible(): void {
    // Select all visible items immediately
    let changed = false;

    // Use for...of loop instead of forEach to allow break statement
    for (const item of this.filteredOptions) {
      if (!this.filterGroup[item.value]) {
        this.filterGroup[item.value] = true;
        this.selectCount++;

        // Update caches
        if (!this.selectedItemsCache.find(cached => cached.value === item.value)) {
          this.selectedItemsCache.push(item);
        }
        if (!this.allOptions.find(option => option.value === item.value)) {
          this.allOptions.push(item);
        }

        changed = true;

        if (this.isSingle) {
          break;
        }
      }
    }

    if (changed) {
      this.updateFormValue();
      this.updateSelectedItems();
      this.emitChange();
      this.onTouched();
      this.updateBulkUI();
      setTimeout(() => this.updateCheckboxes(), 0);
    }
  }

  private updateBulkUI(): void {
    // Update bulk selection UI elements
    const selectedSpan = document.querySelector('.bulk-header .selected') as HTMLElement;
    const selectAllBtn = document.querySelector('.btn-select-all') as HTMLButtonElement;
    const clearBtn = document.querySelector('.btn-clear') as HTMLButtonElement;
    const resultsCount = document.querySelector('.bulk-header .results-count') as HTMLElement;

    // Update results count
    if (resultsCount) {
      const visibleCount = this.filteredOptions.length;
      resultsCount.textContent = `${visibleCount} results`;
    }

    // Update selected count display
    const selectedCount = this.getSelectedItems().length;
    if (selectedSpan) {
      if (selectedCount > 0) {
        selectedSpan.textContent = `• ${selectedCount} selected`;
        selectedSpan.style.display = 'inline';
      } else {
        selectedSpan.style.display = 'none';
      }
    }

    // Update button states
    if (selectAllBtn) {
      const selectableCount = this.getSelectableCount();
      selectAllBtn.textContent = `Select All (${selectableCount})`;
      selectAllBtn.disabled = this.areAllSelectableSelected() || selectableCount === 0;
    }

    if (clearBtn) {
      clearBtn.disabled = selectedCount === 0;
      clearBtn.textContent = 'Clear All';
    }
  }

  private updateCheckboxes(): void {
    // Update checkbox states in dropdown
    const checkboxes = document.querySelectorAll('.p-autocomplete-overlay .checkbox');
    const items = document.querySelectorAll('.p-autocomplete-overlay .p-autocomplete-option');

    checkboxes.forEach((checkbox, index) => {
      const dataset = this.filteredOptions[index];
      if (dataset) {
        const isSelected = !!this.filterGroup[dataset.value];

        // Force update the checkbox state
        (checkbox as HTMLInputElement).checked = isSelected;

        // Update parent item visual state
        const item = items[index] as HTMLElement;
        if (item) {
          if (isSelected) {
            item.classList.add('selected-item');
          } else {
            item.classList.remove('selected-item');
          }
        }
      }
    });
  }

  private getSelectableCount(): number {
    // Get count of items that can be selected
    return this.filteredOptions.filter(item => !this.filterGroup[item.value]).length;
  }

  // ========== EVENT HANDLERS ==========
  onSelect(event: any): void {
    // Prevent PrimeNG's default selection when using checkboxes in multi-select mode
    if (this.allowMultipleFromSearch && !this.isSingle) {
      // For multi-select with checkboxes, ignore PrimeNG's onSelect
      // Selection is handled by toggleSelection method through checkboxes
      return;
    }

    // Handle direct item selection from dropdown (for single select or non-checkbox modes)
    const selectedItem = event.value || event;
    if (!selectedItem?.value) return;

    const value = Number(selectedItem.value);

    if (this.isSingle) {
      this.clearAllSelections();
    }

    this.filterGroup[value] = true;
    this.selectCount++;

    // Update caches
    if (!this.selectedItemsCache.find(cached => cached.value === value)) {
      this.selectedItemsCache.push(selectedItem);
    }
    if (!this.allOptions.find(option => option.value === value)) {
      this.allOptions.push(selectedItem);
    }

    this.searchControl.setValue('');
    this.clearSearchState();
    this.updateFormValue();
    this.updateSelectedItems();
    this.emitChange();
    this.onTouched();
  }

  onClear(): void {
    // Handle clear button click
    this.clearAllSelections();
    this.clearSearchState();
    this.onTouched();
  }

  onFocus(): void {
    // Handle input focus
    this.showDropdown = true;
    if (this.useExistingDataOnly) {

      if (this.columnset && this.columnset.length > 0) {
        this.allOptions = [...this.columnset];
        this.filteredOptions = [...this.allOptions];
      }
      // this.filteredOptions = [...this.allOptions]; // ← SHOW ALL OPTIONS
      this.showNoResults = this.filteredOptions.length === 0;
      // ✅ For single select, immediately show the dropdown with options
      // TODO: check if isSingle only
      if (this.isSingle && this.filteredOptions.length > 0 && this.autoComplete) {
        setTimeout(() => {
          this.autoComplete.show();
          setTimeout(() => this.enhanceDropdown(), 50);
        }, 100);
      }
    }
  }

  onBlur(): void {
    // Handle input blur
    this.showDropdown = false;
    this.onTouched();
  }

  onKeyUp(event: any): void {
    // Handle keyboard input
    const query = event.target.value?.trim() || '';

    if (event.key === 'Escape') {
      this.clearSearchState();
      return;
    }

    // Backspace to remove last selected item
    if (event.key === 'Backspace' && query.length === 0 && this.getSelectedItems().length > 0) {
      const selectedItems = this.getSelectedItems();
      const lastItem = selectedItems[selectedItems.length - 1];
      this.removeSelectedItem(lastItem);
      return;
    }

    if (query.length >= 2) {
      this.search({ query });
    } else if (query.length < 2) {
      this.clearSearchState();
    }
  }

  removeSelectedItem(item: Dataset): void {
    // Remove a selected item
    const value = Number(item.value);
    this.filterGroup[value] = false;
    this.selectCount--;

    this.selectedItemsCache = this.selectedItemsCache.filter(cached => cached.value !== value);

    if (this.isSingle) {
      this.selectCount = 0;
    }

    this.updateFormValue();
    this.updateSelectedItems();
    this.emitChange();
    this.onTouched();
  }

  // ========== UTILITY METHODS ==========
  trackByValue(index: number, item: Dataset): any {
    // Track by function for ngFor performance
    return item.value;
  }

  private cleanupParams(params: string): string {
    // Clean up API parameters by removing empty values
    if (!params) return '';

    return params
      .split('&')
      .filter(param => {
        if (!param.includes('=')) return false;
        const [key, value] = param.split('=');
        return value !== 'undefined' && value !== 'null' && value !== '' && value != null;
      })
      .map(param => param.startsWith('&') ? param : `&${param}`)
      .join('');
  }

  private parseApiResponse(body: any): {results: Dataset[], total: number, hasMore: boolean} {
    // Parse API response into standardized format
    if (!body) return { results: [], total: 0, hasMore: false };

    // New paginated format
    if (body.results && Array.isArray(body.results)) {
      const results = body.results
        // .filter(option => !this.filterGroup[option.value])
        .map(item => ({ value: item.value, label: item.label }));
      return { results, total: body.total || 0, hasMore: body.hasMore || false };
    }

    // Old format
    if (Array.isArray(body)) {
      const results = body
        // .filter(option => !this.filterGroup[option.value])
        .map(item => ({ value: item.value, label: item.label }));
      return { results, total: results.length, hasMore: false };
    }

    return { results: [], total: 0, hasMore: false };
  }

  private findItemInDatasets(value: any): Dataset | null {
    // Find item in available datasets
    if (this.columnset && this.columnset.length > 0) {
      const item = this.columnset.find(item => item.value === value);
      if (item) return item;
    }

    if (this.allOptions && this.allOptions.length > 0) {
      const item = this.allOptions.find(item => item.value === value);
      if (item) return item;
    }

    if (this.selectedItemsCache && this.selectedItemsCache.length > 0) {
      const item = this.selectedItemsCache.find(item => item.value === value);
      if (item) return item;
    }

    return null;
  }

  // ========== STATE MANAGEMENT METHODS ==========
  private updateSelectedItems(): void {
    // Update selectedItems array from filterGroup
    this.selectedItems = [];
    for (const key in this.filterGroup) {
      if (this.filterGroup[key] === true) {
        const numericKey = Number(key);
        if (!isNaN(numericKey)) {
          let item = this.findItemInDatasets(numericKey);
          if (!item) {
            item = { value: numericKey, label: `${numericKey}` };
          }
          this.selectedItems.push(item);

          // Add to cache if not present
          if (!this.selectedItemsCache.find(cached => cached.value === numericKey)) {
            this.selectedItemsCache.push(item);
          }
        }
      }
    }
  }

  private clearAllSelectionsWithoutEmit(): void {
    this.selectCount = 0;
    for (const key in this.filterGroup) {
      this.filterGroup[key] = false;
    }
    this.selectedItemsCache = [];
  }

  private clearAllSelections(): void {
    // Clear all selections and reset state
    this.selectCount = 0;
    for (const key in this.filterGroup) {
      this.filterGroup[key] = false;
    }
    this.selectedItemsCache = [];
    this.updateFormValue();
    this.updateSelectedItems();
    this.emitChange();
  }

  private updateFormValue(): void {
    // Update form control value
    const selectedItems = this.allOptions.filter(option => this.filterGroup[option.value]);
    if (this.isSingle) {
      this.control.setValue(selectedItems.length > 0 ? selectedItems[0] : null);
    } else {
      this.control.setValue(selectedItems);
    }
  }

  private emitChange(): void {
    // Emit change events to parent components
    const emitVal: number[] = [];
    for (const key in this.filterGroup) {
      if (this.filterGroup[key] === true) {
        const numericKey = Number(key);
        if (!isNaN(numericKey)) {
          emitVal.push(numericKey);
        }
      }
    }

    if (this.isSingle) {
      const value = emitVal.length > 0 ? emitVal[0] : undefined;
      this.onChanged(value);
    } else {
      this.onChanged(emitVal);
    }

    this.onChange.emit(emitVal);
  }
}
